import type { ProfitPlusPedidoRecord } from '../../models/integrations/profitPlus2k8';
import type { ProfitPlusBlob, SubAlmacenOption } from './types';

/**
 * Line-level Profit blob: canonical `profitPlus2k8`, plus legacy keys from older writes.
 */
export function orderLineProfitPlus2k8FromPayload(
  p: unknown
): ProfitPlusBlob | undefined {
  if (!p || typeof p !== 'object') return undefined;
  const o = p as Record<string, unknown>;
  const raw =
    o.profitPlus2k8 ??
    o.profitplus2k8 ??
    (o as { profitplus2K8?: unknown }).profitplus2K8 ??
    (o as { profit_plus_2k8?: unknown }).profit_plus_2k8;
  if (!raw || typeof raw !== 'object') return undefined;
  return raw as ProfitPlusBlob;
}

/** Sale-level `profitPlus2k8` on order docs (list snapshots / legacy keys). */
export function orderDocumentProfitPlus2k8FromOrder(
  o: unknown
): ProfitPlusBlob | undefined {
  if (!o || typeof o !== 'object') return undefined;
  const doc = o as Record<string, unknown>;
  const raw =
    doc.profitPlus2k8 ??
    doc.profitplus2k8 ??
    (doc as { profitplus2K8?: unknown }).profitplus2K8 ??
    (doc as { profit_plus_2k8?: unknown }).profit_plus_2k8;
  if (!raw || typeof raw !== 'object') return undefined;
  return raw as ProfitPlusBlob;
}

/** Normalized line sub-almacén (Profit `co_sub`) from an order line payload or product row. */
export function orderLineCoSubFromPayload(p: unknown): string | undefined {
  const pp = orderLineProfitPlus2k8FromPayload(p);
  const f = pp?.fields;
  const o = p && typeof p === 'object' ? (p as Record<string, unknown>) : {};
  const raw =
    f?.co_sub ??
    f?.coSub ??
    f?.co_alma ??
    o.co_sub ??
    o.coSub ??
    o.co_alma;
  const s = raw == null ? '' : String(raw).trim();
  return s || undefined;
}

/**
 * Shallow order snapshots may omit pedido fields; prefer non-empty values from `getOrderServer`.
 */
export function applyAuthoritativePedidoFieldsFromServer(
  prev: Partial<ProfitPlusPedidoRecord>,
  server: Record<string, unknown>
): Partial<ProfitPlusPedidoRecord> {
  const next = { ...prev };
  let changed = false;
  const takeStr = (key: keyof ProfitPlusPedidoRecord) => {
    const sv = server[key as string];
    if (sv === undefined || sv === null) return;
    const t = String(sv).trim();
    if (!t) return;
    if (String(next[key] ?? '').trim() !== t) {
      (next as Record<string, unknown>)[key as string] = t;
      changed = true;
    }
  };
  takeStr('co_sucu');
  takeStr('co_tran');
  takeStr('forma_pag');
  takeStr('moneda');
  const st = server.tasa;
  if (st != null && Number.isFinite(Number(st)) && Number(st) > 0) {
    const n = Number(st);
    if (next.tasa !== n) {
      next.tasa = n;
      changed = true;
    }
  }
  return changed ? next : prev;
}

/**
 * So `<Form.Select value={co_sub}>` works when the code is on the sale line but not yet in
 * reference tables (sync lag) or while refs are still loading.
 */
export function subAlmacenOptionsIncludingSavedSelection(
  opts: SubAlmacenOption[],
  selectedCoSub?: string
): SubAlmacenOption[] {
  const sub = String(selectedCoSub ?? '').trim();
  if (!sub) return opts;
  if (opts.some(o => o.coSub === sub)) return opts;
  return [
    ...opts,
    {
      coSub: sub,
      coAlma: '',
      description: '',
      label: `${sub} (en la venta)`,
    },
  ];
}
